import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ViewChildren,
  ElementRef,
  QueryList,
  ContentChild,
  TemplateRef,
  AfterViewInit,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ListKeyManager } from '@angular/cdk/a11y';

import { FocusManagerService } from '../../../accessibility/focus-manager.service';
import { TranslateHelperService } from '../../../translation/services/translate-helper.service';
import { isMainButtonPressed } from '../../../utils/event';
import { FocusableComponent } from '../../../accessibility/focusable/focusable.component';
import { IBaseCapsuleOverflowConfig, IBaseCapsuleItem } from './interfaces';
import { resources } from './resources';
@Component({
  selector: 'app-vi-base-capsule-list',
  templateUrl: './base-capsule-list.component.html',
  styleUrls: ['./base-capsule-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseCapsuleListComponent<T extends IBaseCapsuleItem = IBaseCapsuleItem>
  extends FocusableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() public items: T[] = [];
  @Input() public edit = false;
  @Input() public activeContainer = false;
  @Input() public timeline = false;
  @Input() public dir = 'ltr';
  @Input() public listLabel = '';
  @Input() public conf: IBaseCapsuleOverflowConfig;
  @Input() public enableAddNewItem = true;
  @Input() public textFilter = '';
  @Input() public enableSelection = true;
  @Input() public disabled = false;

  @Output() public itemSelected = new EventEmitter<T>();
  @Output() public toggleExpandList = new EventEmitter<void>();
  @Output() public add = new EventEmitter<string>();
  @Output() public remove = new EventEmitter<number | string>();

  // Legacy injected input slot (kept for backward compatibility with insights)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('injectedInput', { static: false }) public injectedInputTemplate?: TemplateRef<any>;
  // Custom add capsule UI override
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('addCapsuleTemplate', { static: false }) public customAddTemplate?: TemplateRef<any>;

  @ViewChild('itemObject', { static: false }) public itemObject?: ElementRef;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChildren('itemObject') public listItems!: QueryList<any>;

  public resources = resources;
  public placeholderText = '';
  public placeholderEnterText = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected keyboardEventsManager?: ListKeyManager<any>;

  constructor(protected focusManager: FocusManagerService, public cdr: ChangeDetectorRef, protected translate: TranslateHelperService) {
    super();
  }

  public ngOnInit(): void {
    this.translate.translateResources(this.resources);
  }

  public ngAfterViewInit(): void {
    this.keyboardEventsManager = new ListKeyManager(this.listItems);
    this.keyboardEventsManager.setFirstItemActive();
  }

  public ngOnChanges(_changes: SimpleChanges): void {}

  public getCapsuleId(item: T): string {
    return `capsuleListItemId${item?.id}`;
  }

  public getAnnounceText(item: T): string {
    return this.getItemName(item);
  }

  public hasCustomClass(item: T): boolean {
    return false;
  }

  public selectItem(item: T): void {
    this.itemSelected.emit(item);
  }

  public getIsItemSelected(item: T): boolean {
    return this.enableSelection && !!(item as IBaseCapsuleItem).selected;
  }

  public isItemEditable(item: T): boolean {
    return this.edit && this.allowRemove(item);
  }

  public handleAddCapsule(input: string): void {
    this.add.emit(input);
  }

  public removeItem(item: T, $event?): void {
    this.remove.emit(item.id);
    if ($event) {
      $event.stopPropagation();
      if ($event.type !== 'keyup') {
        return;
      }
    }

    const currentObj = $event.target.parentNode;

    // Find previous item at list
    const currentArray = this.listItems.toArray();

    let index;
    for (index = 0; index < currentArray.length; index++) {
      const element: ElementRef = currentArray[index];
      if (element.nativeElement === currentObj) {
        break;
      }
    }

    this.focusManager.focusVia(() => {
      // Update view
      this.cdr.detectChanges();
      const listItmes = this.listItems.toArray();
      return listItmes[index - 1];
    }, this.cdr);
  }

  public toggleExpandMouse($event: MouseEvent): void {
    if (isMainButtonPressed($event)) {
      this.toggleExpand();
    }
  }

  public toggleExpand(bFromEnter = false): void {
    const currentLength: number = this.listItems?.toArray().length ?? 0;
    this.toggleExpandList.emit();

    if (bFromEnter) {
      this.focusManager.focusVia(() => {
        this.cdr.detectChanges();
        const listItems = this.listItems.toArray();
        return listItems[currentLength - 1];
      }, this.cdr);
    }

    this.cdr.detectChanges();
  }

  public markForCheck(): void {
    this.cdr.markForCheck();
  }

  public trackByIndex(i: number): number {
    return i;
  }

  public getItemName(item: T): string {
    return item.value;
  }

  protected allowRemove(_item: T): boolean {
    return !this.disabled;
  }
}
